import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users, LogOut, Sparkles, Loader2, Rocket, FolderKanban,
  Clock, ArrowRight, Wallet, FlaskConical
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import FinanceSummary from '@/components/FinanceSummary';
import IndustryLabs from '@/components/IndustryLabs';

const STATUS_LABEL = {
  drafting: { label: 'AI Drafting', color: 'bg-blue-500/10 text-blue-600' },
  proposal_ready: { label: 'Proposal Ready', color: 'bg-amber-500/10 text-amber-600' },
  accepted: { label: 'Notifying Experts', color: 'bg-purple-500/10 text-purple-600' },
  interest_open: { label: 'Awaiting Interest', color: 'bg-purple-500/10 text-purple-600' },
  admin_selecting: { label: 'Admin Reviewing', color: 'bg-cyan-500/10 text-cyan-600' },
  expert_selected: { label: 'Payment Due', color: 'bg-orange-500/10 text-orange-600' },
  payment_received: { label: 'Payment Received', color: 'bg-teal-500/10 text-teal-600' },
  in_progress: { label: 'In Progress', color: 'bg-green-500/10 text-green-600' },
  completed: { label: 'Completed', color: 'bg-emerald-600/10 text-emerald-700' },
  cancelled: { label: 'Cancelled', color: 'bg-zinc-500/10 text-zinc-600' },
};

export default function IndustryProjects() {
  const navigate = useNavigate();
  const { isAuthenticated, isIndustry, user, token, logout, API, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isIndustry) {
      navigate('/industry/auth');
      return;
    }
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, isIndustry]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitPrompt = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || prompt.trim().length < 20) {
      toast.error('Describe your requirement in at least a couple of sentences.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: prompt.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Drafting your proposal…');
        navigate(`/industry/projects/${data.id}`);
      } else {
        toast.error('Could not create project');
      }
    } catch (e) {
      toast.error('Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || !isAuthenticated) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen">
      <nav className="nav-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md gradient-primary flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-semibold tracking-tight">CollabHive</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">Powered by PM</span>
            </div>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="hidden sm:inline text-sm text-muted-foreground">{user?.company_name || user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="gap-2" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </nav>

      <main className="container-main py-6 sm:py-8 max-w-4xl">
        <Tabs defaultValue="projects" className="w-full">
          <TabsList className="grid grid-cols-3 w-full max-w-md mb-5 h-auto">
            <TabsTrigger value="projects" className="gap-2 text-xs sm:text-sm py-2" data-testid="tab-projects">
              <FolderKanban className="w-4 h-4" /> Projects
            </TabsTrigger>
            <TabsTrigger value="labs" className="gap-2 text-xs sm:text-sm py-2" data-testid="tab-labs">
              <FlaskConical className="w-4 h-4" /> Labs
            </TabsTrigger>
            <TabsTrigger value="finance" className="gap-2 text-xs sm:text-sm py-2" data-testid="tab-finance">
              <Wallet className="w-4 h-4" /> Finance
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="mt-0 space-y-6">
        {/* Prompt Box */}
        <Card className="mb-8 animate-fade-in border-primary/40 bg-primary/5">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">Start a new project</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Describe what you need. CollabHive will turn it into a complete Requirement Brief
              with scope, timeline, IP terms and budget — ready for you to review and refine.
            </p>
            <form onSubmit={submitPrompt} className="space-y-3">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., I need to develop a novel cathode material for lithium-ion batteries with 30% higher energy density. Timeline 6 months, budget ~30 lakhs. Prefer someone with battery lab access."
                rows={5}
                className="resize-none"
                data-testid="project-prompt-input"
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={submitting} className="gap-2" data-testid="create-project-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  Generate Brief
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Projects List */}
        <div className="flex items-center gap-2 mb-4">
          <FolderKanban className="w-5 h-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Your Projects</h3>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground border border-dashed rounded-lg">
            No projects yet. Describe your first requirement above.
          </div>
        ) : (
          <div className="space-y-3">
            {projects.map((p) => {
              const s = STATUS_LABEL[p.status] || STATUS_LABEL.drafting;
              return (
                <Card key={p.id} className="hover:border-primary/30 transition-colors cursor-pointer" onClick={() => navigate(`/industry/projects/${p.id}`)} data-testid={`project-card-${p.id}`}>
                  <CardContent className="p-4 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-medium truncate">{p.title}</h4>
                        <Badge className={s.color}>{s.label}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{p.prompt}</p>
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Updated {new Date(p.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0 mt-2" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
          </TabsContent>

          <TabsContent value="labs" className="mt-0">
            <IndustryLabs API={API} token={token} user={user} />
          </TabsContent>

          <TabsContent value="finance" className="mt-0">
            <FinanceSummary API={API} token={token} defaultRole="industry" />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
